import { Injectable, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { PrimeNG } from 'primeng/config';
import { combineLatest, fromEvent, switchMap, tap } from 'rxjs';
import { LocalStorageClass } from '../../helpers/local-storage-helper';
import { AuthService } from '../auth/auth.service';
import { AnalyticsService } from './analytics.service';
import { TenantConfigurationService } from './tenant-configuration.service';
import { UiService } from './ui.service';
import { UserService } from './user.service';

@Injectable({ providedIn: 'root' })
export class AppInitService {
  initialized = signal(false);

  private readonly configurationService = inject(TenantConfigurationService);
  private readonly uiService = inject(UiService);
  private readonly authService = inject(AuthService);
  private readonly translate = inject(TranslateService);
  private readonly primeNgConfig = inject(PrimeNG);
  private readonly userService = inject(UserService);
  private readonly analyticsService = inject(AnalyticsService);

  setupMobileDetection(): void {
    fromEvent(window, 'resize').subscribe(() => {
      this.uiService.updateMobileState();
    });
  }

  configureTranslateService() {
    this.translate.addLangs(['en', 'hr', 'bs', 'sr-Latn', 'de', 'it', 'sl', 'pt']);
    this.translate.setFallbackLang('en');
    this.translate.use(LocalStorageClass.getSelectedLanguage());

    this.translate.get('primeNg').subscribe((res) => this.primeNgConfig.setTranslation(res));
  }

  initializeAppOnStartup(): void {
    this.configureTranslateService();
    this.setupMobileDetection();

    const token = LocalStorageClass.retrieveTokenFromStorage();

    if (!token) return;

    this.uiService.toggleSpinner(true);

    const shouldRefreshToken =
      !!LocalStorageClass.getRefreshToken() &&
      LocalStorageClass.shouldRefreshToken() &&
      LocalStorageClass.isTokenExpiringSoon(5);

    if (shouldRefreshToken) {
      this.refreshToken()
        .pipe(switchMap(() => this.initializeApp()))
        .subscribe();
    } else {
      this.initializeApp().subscribe();
    }
  }

  initializeApp() {
    return combineLatest([
      this.configurationService.getGlobalApplicationConfiguration(),
      this.userService.getMeData(),
    ]).pipe(
      tap(([, user]) => {
        this.analyticsService.trackEvent(`user_audience_${user.tenant}`);
        this.initialized.set(true);
        this.uiService.toggleSpinner(false);
        this.setupAutoTokenRefresh();
        this.authService
          .getCurrentUserTenants()
          .pipe(tap((response) => this.authService.tenants.set(response.tenants)))
          .subscribe();
      }),
    );
  }

  refreshToken() {
    return this.authService.refreshCurrentToken();
  }

  setupAutoTokenRefresh() {
    const newToken = LocalStorageClass.retrieveTokenFromStorage();

    if (!newToken) return;

    // No refresh token → nothing to refresh with; skip the timer entirely.
    if (!LocalStorageClass.getRefreshToken()) return;

    const jwtDecrypted = JSON.parse(atob(newToken.split('.')[1]));

    const expirationDateUnix = jwtDecrypted?.exp;
    const expirationDateMs = expirationDateUnix * 1000;

    const now = Date.now();
    let msUntilExpiration = expirationDateMs - now;

    const twoDaysMs = 48 * 60 * 60 * 1000;

    if (msUntilExpiration > twoDaysMs) {
      return;
    }

    // Clamp: a token expiring within the next ~15s would otherwise produce a
    // non-positive interval, hammering the refresh endpoint in a tight loop.
    const refreshDelayMs = Math.max(msUntilExpiration - 15000, 60000);

    setInterval(() => {
      this.authService.refreshCurrentToken().subscribe();
    }, refreshDelayMs);
  }
}
